/**
 * DR Coupon Reminder
 * 
 * Handles coupon chip interactions in the cart drawer.
 * 
 * VISIBILITY is controlled by Liquid (server-rendered), not JS.
 * This follows the same pattern as the gifts widget - no timing issues.
 * 
 * JS only handles:
 * - Click events (activation, dismiss)
 * - Loading states during activation
 * - Immediate hide after activation (for responsiveness before drawer refresh)
 * 
 * v15: Liquid-controlled visibility (like gifts widget)
 */
(function() {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════════════
  // SINGLETON GUARD
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (window.__drCouponReminderMounted) return;
  window.__drCouponReminderMounted = true;

  // ═══════════════════════════════════════════════════════════════════════════
  // DEBUG MODE
  // ═══════════════════════════════════════════════════════════════════════════
  
  const DEBUG = new URLSearchParams(location.search).has('drCouponDebug');
  
  function debugLog(...args) {
    if (DEBUG) console.log('[DR Coupon]', ...args);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIG
  // ═══════════════════════════════════════════════════════════════════════════
  
  function getConfig() {
    const cfg = window.DR_COUPON_REMINDER;
    if (!cfg) {
      debugLog('No config found');
      return null;
    }
    return {
      code: String(cfg.code || 'DR46').trim().toUpperCase()
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════
  
  let isActivating = false;

  // ═══════════════════════════════════════════════════════════════════════════
  // DOM HELPERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  function getDrawer() {
    return document.querySelector('cart-drawer#mini-cart') ||
           document.querySelector('cart-drawer') ||
           document.querySelector('#mini-cart');
  }

  function getChip(drawer) {
    if (!drawer) drawer = getDrawer();
    if (!drawer) return null;
    return drawer.querySelector('[data-dr-coupon-chip]');
  }

  function hideChip(chip) {
    if (!chip) return;
    chip.setAttribute('hidden', '');
    debugLog('Chip hidden');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTIVATION FLOW
  // Uses reconciler's applyDiscountCode (single-writer queue)
  // ═══════════════════════════════════════════════════════════════════════════
  
  async function activateCoupon() {
    const config = getConfig();
    if (!config) return;

    const chip = getChip();
    const activateBtn = chip?.querySelector('[data-dr-coupon-activate]');
    const code = config.code;

    if (isActivating) return;
    isActivating = true;

    debugLog('Activation started, code:', code);

    // Show loading state
    if (chip) chip.classList.add('dr-coupon-chip--loading');
    if (activateBtn) {
      activateBtn.disabled = true;
      activateBtn.setAttribute('aria-busy', 'true');
    }
    try {
      if (!window.drGiftsReconciler?.applyDiscountCode) {
        console.error('[DR Coupon] Reconciler not available');
        return;
      }

      // Hide chip immediately for responsive UX (before drawer refresh).
      // In the unified gifts widget, keep it visible to show loading state.
      const isUnifiedWidget = Boolean(chip?.querySelector('.dr-gifts__coupon-button'));
      if (!isUnifiedWidget) {
        hideChip(chip);
      }

      // Apply discount via reconciler (will dispatch cart:updated/cart:refresh)
      // The drawer will be refreshed with new HTML where chip is already hidden via Liquid
      await window.drGiftsReconciler.applyDiscountCode(code, { mode: 'append' });
      debugLog('Activation complete');

    } catch (err) {
      console.error('[DR Coupon] Activation error:', err);
      // On error, chip will be re-evaluated on next drawer refresh
    } finally {
      isActivating = false;
      if (chip) chip.classList.remove('dr-coupon-chip--loading');
      if (activateBtn) {
        activateBtn.disabled = false;
        activateBtn.removeAttribute('aria-busy');
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DISMISS HANDLER
  // ═══════════════════════════════════════════════════════════════════════════
  
  function dismissChip() {
    const chip = getChip();
    hideChip(chip);
    debugLog('Dismissed by user');
    // Note: Chip will reappear on next drawer refresh (by design)
    // If you want persistent dismiss, store in sessionStorage
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EVENT LISTENERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  function setupEventListeners() {
    // Click delegation for chip interactions
    document.addEventListener('click', (e) => {
      // Dismiss button
      if (e.target.closest('[data-dr-coupon-dismiss]')) {
        e.preventDefault();
        e.stopPropagation();
        dismissChip();
        return;
      }

      if (e.target.closest('[data-dr-coupon-chip]')) {
        e.preventDefault();
        e.stopPropagation();
        activateCoupon();
        return;
      }
    }, true);

    // Prevent chip interactions from closing drawer
    document.addEventListener('pointerdown', (e) => {
      if (e.target.closest('[data-dr-coupon-chip]')) {
        e.stopPropagation();
      }
    }, true);

    debugLog('Event listeners attached');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INIT
  // ═══════════════════════════════════════════════════════════════════════════
  
  function init() {
    const config = getConfig();
    if (!config) {
      debugLog('Init skipped: no config');
      return;
    }

    debugLog('Init:', config);
    setupEventListeners();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BFCACHE HANDLER
  // On page restore, hide chip immediately to prevent flash.
  // The next drawer refresh will render correct state from Liquid.
  // ═══════════════════════════════════════════════════════════════════════════

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return; // Only handle bfcache restores
    
    debugLog('Page restored from bfcache');
    
    // Hide chip immediately (synchronous, before paint)
    const chip = getChip();
    if (chip) {
      chip.setAttribute('hidden', '');
      debugLog('Chip hidden on bfcache restore');
    }
    
    // Note: Drawer refresh (if drawer is open) will show chip if appropriate
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // INIT
  // ═══════════════════════════════════════════════════════════════════════════

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
